import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShoppingBag } from "lucide-react";

import { cafeteriaService, type DateRange, type MealOrder } from "../../services/cafeteriaService";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, todayIso } from "../../utils/dates";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice } from "../register/RecordParts";
import { OrderForm } from "./CafeteriaForms";
import { errorOf, useNotice, type Setup } from "./MenuPanels";
import { leftOpen, orderActions, orderConfig, orderStatus, paymentLabel, time, ugx, weekday } from "./cafeteriaLogic";

/** The meal days an orders list covers; the server only sends orders for these days. */
export const RANGES: { key: string; label: string; range: () => DateRange }[] = [
  { key: "today", label: "Today", range: () => ({ from: todayIso(), to: todayIso() }) },
  { key: "week", label: "Next 7 days", range: () => ({ from: todayIso(), to: daysFromTodayIso(7) }) },
  { key: "month", label: "Last 30 days", range: () => ({ from: daysFromTodayIso(-30), to: todayIso() }) },
  { key: "around", label: "30 days either side", range: () => ({ from: daysFromTodayIso(-30), to: daysFromTodayIso(30) }) },
];

function RangeBar({ value, onChange }: { value: DateRange; onChange: (next: DateRange) => void }) {
  const preset = RANGES.find((item) => { const range = item.range(); return range.from === value.from && range.to === value.to; })?.key;
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div>
        <p className="mb-1 text-xs font-medium text-gray-500">Meal days</p>
        <div role="group" aria-label="Meal days" className="segmented">
          {RANGES.map((item) => <button key={item.key} type="button" aria-pressed={preset === item.key} onClick={() => onChange(item.range())} className="segmented-option">{item.label}</button>)}
        </div>
      </div>
      <label className="text-xs font-medium text-gray-500">From<input type="date" value={value.from} max={value.to} onChange={(event) => event.target.value && onChange({ ...value, from: event.target.value })} className="input-compact mt-1 block" /></label>
      <label className="text-xs font-medium text-gray-500">To<input type="date" value={value.to} min={value.from} onChange={(event) => event.target.value && onChange({ ...value, to: event.target.value })} className="input-compact mt-1 block" /></label>
    </div>
  );
}

export function OrdersPanel({ kitchen, setup, onChanged, initialView }: { kitchen: boolean; setup: Setup; onChanged: () => void; initialView?: string }) {
  const today = todayIso();
  const [range, setRange] = useState<DateRange>(() => (initialView === "left_open" ? { from: daysFromTodayIso(-30), to: daysFromTodayIso(-1) } : (kitchen ? RANGES[1] : RANGES[3]).range()));
  const orders = useQuery({ queryKey: ["cafeteria", "orders", range], queryFn: () => cafeteriaService.getOrdersIn(range), keepPreviousData: true });
  const { notice, setNotice, done } = useNotice(() => { orders.refetch(); onChanged(); });
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const close = () => { setViewing(null); setCancelling(false); };
  const step = useMutation({
    mutationFn: cafeteriaService.setOrderStatus,
    onSuccess: (saved) => done(`${saved.student_name}: ${orderStatus(saved).label.toLowerCase()}.`),
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The order could not be moved.") }),
  });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => (kitchen ? cafeteriaService.setOrderStatus({ id, status: "cancelled", reason }) : cafeteriaService.cancelOrder({ id, reason })),
    onSuccess: (saved) => { close(); done(`${saved.daily_menu_name} on ${weekday(saved.menu_date)} for ${saved.student_name} cancelled.`); },
  });
  const place = useMutation({ mutationFn: cafeteriaService.createOrder, onSuccess: (saved) => { setForm(false); done(`${saved.daily_menu_name} on ${weekday(saved.menu_date)} ordered for ${saved.student_name}.`); } });

  const columns: RegisterColumn<MealOrder>[] = [
    { header: "Meal day", cell: (row) => <span className={`whitespace-nowrap ${leftOpen(row, today) ? "font-semibold text-red-700" : ""}`}>{weekday(row.menu_date)}</span>, exportValue: (row) => row.menu_date ?? "" },
    { header: "Meal", cell: (row) => row.daily_menu_name, exportValue: (row) => row.daily_menu_name },
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.student_name}</span> <span className="text-xs text-gray-500">{row.student_number}</span></span>, exportValue: (row) => row.student_name },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name ?? "" },
    { header: "Amount", cell: (row) => <span className="tabular-nums">{Number(row.amount).toLocaleString()}</span>, exportValue: (row) => Number(row.amount), align: "right" },
    { header: "Payment", cell: (row) => <span className={row.payment_status === "pending" && row.status !== "cancelled" ? "text-amber-800" : ""}>{paymentLabel(row)}</span>, exportValue: (row) => paymentLabel(row) },
    { header: "Status", cell: (row) => <StatusDot {...orderStatus(row)} />, exportValue: (row) => orderStatus(row).label },
    { header: "Receipt", cell: (row) => row.receipt_number || "", exportValue: (row) => row.receipt_number ?? "", exportOnly: true },
    { header: "Kitchen note", cell: (row) => row.notes || "", exportValue: (row) => row.notes ?? "", exportOnly: true },
  ];
  const row = viewing ? orders.data?.find((item) => item.id === viewing) : undefined;
  const actions = row ? orderActions(row, kitchen) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <RangeBar value={range} onChange={setRange} />
      <ClientRegister
        key={initialView}
        title={kitchen ? "Orders" : "Your orders"}
        description={kitchen
          ? "Meal orders for the days above. Families pay at the bursar's office; paid and meal-plan orders can then be confirmed and served. Use a menu's service sheet at the counter to move many at once."
          : "Meals ordered for your children on the days above. Pay at the bursar's office before the meal; a meal on a meal plan is free."}
        actions={<button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { place.reset(); setForm(true); }}><ShoppingBag className="h-4 w-4" /> {kitchen ? "Take an order" : "Order a meal"}</button>}
        rows={orders.data}
        config={orderConfig(today, kitchen)}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => { setCancelling(false); setViewing(item.id); }}
        openLabel={(item) => orderActions(item, kitchen).next?.label ?? "Open"}
        noun={["order", "orders"]}
        exportName={{ file: "cafeteria-orders", title: "Cafeteria Orders" }}
        isLoading={orders.isLoading}
        isError={orders.isError}
        onRetry={() => orders.refetch()}
        emptyText="No orders for these days."
      />
      {row && actions && (
        <DetailsModal
          title={row.student_name}
          headline={{ label: `${row.daily_menu_name} · ${weekday(row.menu_date)}`, value: ugx(row.amount), note: paymentLabel(row) }}
          status={orderStatus(row)}
          notice={leftOpen(row, today)
            ? <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">This meal day has passed but the order was never served or cancelled.</p>
            : row.status === "pending" && row.payment_status === "pending"
              ? <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{kitchen ? "Waiting for the bursar to receipt the payment (Finance → Cafeteria Orders). It cannot be confirmed before then." : "Pay at the bursar's office before the meal. The kitchen confirms it once it is paid."}</p>
              : actions.needsRefund && kitchen
                ? <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">This order is paid, so it can only be cancelled after the bursar refunds it.</p>
                : undefined}
          facts={[
            ["Student ID", row.student_number || "—"], ["Class", row.class_name || "—"],
            ["Ordered", `${row.order_date}${row.order_time ? ` at ${time(row.order_time)}` : ""}`], ["Receipt", row.receipt_number || "—"],
            ["Meal plan", row.plan_name || "—"], ["Served", row.status === "served" ? `${time(row.served_time)} by ${row.served_by_name}` : "—"],
            ...(row.status === "cancelled" ? [["Cancelled", `${row.cancelled_date ?? ""} by ${row.cancelled_by_name || "—"}`] as [string, string]] : []),
          ]}
          sections={[{ title: "Note for the kitchen", body: row.notes }, { title: "Why it was cancelled", body: row.cancellation_reason, tone: "warning" }]}
          onClose={close}
          footer={
            <>
              {actions.cancel && <button type="button" className="mr-auto rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50" onClick={() => setCancelling(true)}>Cancel order</button>}
              <button type="button" className="btn btn-outline" onClick={close}>Close</button>
              {actions.next && <button type="button" disabled={step.isPending} className="btn btn-primary" onClick={() => step.mutate({ id: row.id, status: actions.next!.status })}>{actions.next.label}</button>}
            </>
          }
        >
          {cancelling && <ReasonPanel title="Cancel the order" label="Reason" placeholder={kitchen ? "e.g. Learner sent home sick" : "e.g. Our child will be away that day"} context={{ key: "cafeteria.note", params: { kind: "order_cancel", id: row.id } }} button="Cancel order" danger busy={cancel.isPending} error={cancel.error} errorText="It could not be cancelled." onSubmit={(reason) => cancel.mutate({ id: row.id, reason })} />}
        </DetailsModal>
      )}
      {form && <OrderForm kitchen={kitchen} learners={setup.learners} menus={setup.menus} busy={place.isPending} error={errorOf(place, "The order could not be placed.")} onSave={(data) => place.mutate(data)} onClose={() => setForm(false)} />}
    </div>
  );
}
